/**
 * Pure helpers for friends' activity rows, reused by the Tonight feed
 * (`features/tonight/feed-model.ts`). Kept dependency-free and unit-testable,
 * mirroring the mobile contract in `app/src/utils/feed.ts`: adjacent episode
 * events from the same friend on the same title collapse into one binge group
 * when every consecutive pair is within the 6-hour window. Comments are merged
 * by the Tonight feed model, not here.
 */

import type { UserActivityItem } from '@shared/types';

import { tForLocale, type Locale } from '@/i18n/locale';

/** Binge window: adjacent same-user episode events up to this far apart group. */
export const BINGE_WINDOW_MS = 6 * 60 * 60 * 1000;

export type ActivityRow<T extends UserActivityItem = UserActivityItem> =
  | { kind: 'single'; item: T }
  | { kind: 'group'; items: T[] };

function timestamp(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

function groupable(item: UserActivityItem): boolean {
  return (
    item.kind === 'episode' &&
    item.friend !== undefined &&
    item.seasonNumber !== undefined &&
    item.episodeNumber !== undefined
  );
}

/**
 * Collapse binge runs in an already newest-first activity list. A run groups
 * only while the friend, the title, and the consecutive-timestamp gap all
 * match; anything else starts a new row.
 */
export function groupActivityRows<T extends UserActivityItem>(items: T[]): ActivityRow<T>[] {
  const rows: ActivityRow<T>[] = [];
  let index = 0;
  while (index < items.length) {
    const first = items[index];
    if (!groupable(first)) {
      rows.push({ kind: 'single', item: first });
      index += 1;
      continue;
    }

    const group: T[] = [first];
    let nextIndex = index + 1;
    while (nextIndex < items.length) {
      const next = items[nextIndex];
      if (!groupable(next)) break;
      if (next.friend!.publicUserId !== first.friend!.publicUserId) break;
      if (next.title.id !== first.title.id) break;
      const gap = Math.abs(
        timestamp(group[group.length - 1].watchedAt) - timestamp(next.watchedAt),
      );
      if (gap > BINGE_WINDOW_MS) break;
      group.push(next);
      nextIndex += 1;
    }

    rows.push(
      group.length > 1 ? { kind: 'group', items: group } : { kind: 'single', item: first },
    );
    index = nextIndex;
  }
  return rows;
}

/** The newest timestamp in a binge group drives its meta line. */
export function newestWatchedAt(items: UserActivityItem[]): string {
  return items.reduce(
    (latest, item) => (timestamp(item.watchedAt) > timestamp(latest) ? item.watchedAt : latest),
    items[0]?.watchedAt ?? '',
  );
}

/** `S2E5` — the app's compact season/episode label. */
export function seasonEpisodeLabel(season: number, episode: number): string {
  return `S${season}E${episode}`;
}

/** The row verb key: watched / rewatched / finished (same vocabulary as the app). */
export function activityVerbKey(kind: UserActivityItem['kind']): string {
  if (kind === 'rewatch') return 'friends.verb.rewatched';
  if (kind === 'title') return 'friends.verb.finished';
  return 'friends.verb.watched';
}

/**
 * The secondary meta line for an activity row: episode marker (when present)
 * plus relative time, "Film · 3h ago" for movies.
 */
export function activityMetaLine(
  item: UserActivityItem,
  now: number = Date.now(),
  locale: Locale = 'en',
): string {
  const time = relativeTime(item.watchedAt, now, locale);
  if (
    (item.kind === 'episode' || item.kind === 'rewatch') &&
    item.seasonNumber !== undefined &&
    item.episodeNumber !== undefined
  ) {
    return `${seasonEpisodeLabel(item.seasonNumber, item.episodeNumber)} · ${time}`;
  }
  if (item.kind === 'movie') {
    return time
      ? tForLocale(locale, 'friends.filmMeta', { time })
      : tForLocale(locale, 'common.film');
  }
  return time;
}

/**
 * A binge group's summary: up to three episode labels, newest first, then a
 * "+N more" tail (mirrors the app's `formatEpisodeSummary`).
 */
export function episodeSummary(items: UserActivityItem[], locale: Locale = 'en'): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const item of items) {
    if (item.seasonNumber === undefined || item.episodeNumber === undefined) continue;
    const label = seasonEpisodeLabel(item.seasonNumber, item.episodeNumber);
    if (seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  const shown = labels.slice(0, 3);
  const remaining = Math.max(0, labels.length - shown.length);
  const summary = shown.join(' · ');
  return remaining > 0
    ? tForLocale(locale, 'friends.episodeSummaryMore', { summary, count: remaining })
    : summary;
}

/** "just now", "12m ago", "3h ago", "2d ago", then a short calendar date. */
export function relativeTime(
  iso: string,
  now: number = Date.now(),
  locale: Locale = 'en',
): string {
  const then = timestamp(iso);
  if (!then) return '';
  const diffMs = Math.max(0, now - then);
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return tForLocale(locale, 'time.justNow');
  if (minutes < 60) return tForLocale(locale, 'time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tForLocale(locale, 'time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return tForLocale(locale, 'time.daysAgo', { count: days });
  const date = new Date(then);
  const sameYear = date.getUTCFullYear() === new Date(now).getUTCFullYear();
  return date.toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Initials for an avatar fallback: first letter of the first two words. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const letters = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
  return letters || '?';
}
